import { env } from "@/env";
import {
	type ApiQueryAddonTier,
	QUERY_ADDON_TIERS_FALLBACK,
} from "@/utils/api-pricing";
import { getAccessToken } from "@/utils/local-storage";

export type BillingApiPricingDetails = {
	plan_name: string;
	base_api_requests: number;
	addon_api_requests: number;
	effective_api_requests: number;
	rate_limit_requests: number;
	rate_limit_window_seconds: number;
	stripe_addon_price_id?: string;
};

type ApiEnvelope<T> = {
	code: string;
	message?: string;
	data?: T;
};

async function billingRequest<T>(
	path: string,
	init?: RequestInit,
): Promise<ApiEnvelope<T>> {
	const token = getAccessToken();
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
		...(init?.headers as Record<string, string> | undefined),
	};
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}
	const response = await fetch(`${env.VITE_BACKEND_URL}${path}`, {
		...init,
		headers,
	});
	const body = (await response.json()) as ApiEnvelope<T>;
	if (!response.ok) {
		throw new Error(body.message || "Billing request failed");
	}
	return body;
}

export async function fetchApiQueryAddons(): Promise<ApiQueryAddonTier[]> {
	try {
		const response = await billingRequest<ApiQueryAddonTier[]>(
			"/api/billing/query-addons",
		);
		if (response.code !== "SUCCESS" || !response.data?.length) {
			return QUERY_ADDON_TIERS_FALLBACK;
		}
		return response.data;
	} catch {
		// Backend may return 404 when modules.yaml is missing in Docker (api_pricing off).
		return QUERY_ADDON_TIERS_FALLBACK;
	}
}

export async function setApiQueryAddon(
	teamId: string,
	priceId: string,
): Promise<BillingApiPricingDetails | null> {
	const response = await billingRequest<BillingApiPricingDetails>(
		`/api/billing/${teamId}/addon`,
		{
			method: "POST",
			body: JSON.stringify({ price_id: priceId }),
		},
	);
	if (response.code !== "SUCCESS") {
		throw new Error(response.message || "Failed to update add-on");
	}
	return response.data ?? null;
}

export async function removeApiQueryAddon(
	teamId: string,
): Promise<BillingApiPricingDetails | null> {
	const response = await billingRequest<BillingApiPricingDetails>(
		`/api/billing/${teamId}/addon`,
		{ method: "DELETE" },
	);
	if (response.code !== "SUCCESS") {
		throw new Error(response.message || "Failed to remove add-on");
	}
	return response.data ?? null;
}
